import { apiClient } from './apiClient';
import type { Entity, EntityConnection } from '../../types/api';

// Helper to map entity response to frontend Entity interface
const mapEntityResponse = (entity: any): Entity => {
  return {
    id: entity.uuid || entity.id,
  name: entity.primary_id || entity.name,
  concept_id: entity.component_type || entity.concept_id,
  site_id: entity.site_id,
  tenant_id: entity.tenant_id || '',
  parent_entity_id: entity.parent_entity_id || null,
  description: entity.description || null,
  manufacturer: entity.manufacturer || null,
  model: entity.model || null,
  serial_number: entity.serial_number || null,
  installation_date: entity.installation_date || null,
  warranty_expiry: entity.warranty_expiry || null,
  status: entity.status || 'active',
  latitude: entity.latitude || null,
  longitude: entity.longitude || null,
  altitude: entity.altitude || null,
  orientation: entity.orientation || null,
  tilt: entity.tilt || null,
  properties: entity.properties || {},
  version: entity.version || 1,
  created_at: entity.created_at || new Date().toISOString(),
  updated_at: entity.updated_at || new Date().toISOString(),
  // Legacy field mappings for compatibility
  entity_id: entity.uuid || entity.id,
  entity_name: entity.primary_id || entity.name,
  entity_type_id: entity.component_type || entity.concept_id,
  entity_type_name: entity.component_type || entity.concept_name,
  concept_name: entity.component_type || entity.concept_name,
  concept_category: entity.component_type || entity.concept_category,
  pvcollada_component_type: entity.component_type || entity.pvcollada_component_type,
  metadata: entity.properties || {},
  entity_metadata: entity.properties || {},
  component_order: entity.component_order,
  aliases: entity.aliases || [],
  configuration: entity.configuration || {},
  pref: entity.pref,
  };
};

// Helper to map connection response to frontend EntityConnection interface
const mapConnectionResponse = (conn: any): EntityConnection => {
  return {
  id: conn.id,
  tenant_id: conn.tenant_id || '',
  site_id: conn.site_id,
  source_entity_id: conn.source_entity_id,
  target_entity_id: conn.target_entity_id,
  connection_type: conn.relation || conn.connection_type || 'connects',
  properties: {
    ...(conn.properties || {}),
    signal: conn.signal,
    pref: conn.pref,
  },
  configuration: conn.configuration || {},
  created_at: conn.created_at || new Date().toISOString(),
  source_entity_name: conn.source_entity_name,
  target_entity_name: conn.target_entity_name,
  };
};

export interface ListEntitiesParams {
  portfolio_id: string;
  site_id: string;
  limit?: number;
  offset?: number;
  exclude_modules?: boolean;
}

export interface ListConnectionsParams {
  portfolio_id: string;
  site_id: string;
  limit?: number;
  offset?: number;
}

export interface ListEntitiesIncrementalParams {
  portfolio_id: string;
  site_id: string;
  limit: number;
  offset: number;
  concept_id?: string;
}

export interface ListEntitiesPageParams {
  portfolio_id: string;
  site_id: string;
  limit: number;
  offset?: number;
  exclude_modules?: boolean;
}

export interface ListConnectionsForEntitiesParams {
  portfolio_id: string;
  site_id: string;
  entity_ids: string[];
}

export interface EntitiesResponse {
  items: Entity[];
  total: number;
  limit: number;
  offset: number;
}

export interface ConnectionsResponse {
  items: EntityConnection[];
  total: number;
  limit: number;
  offset: number;
}

export interface GetSiteGraphParams {
  portfolio_id: string;
  site_id: string;
  exclude_modules?: boolean;
}

export interface SiteGraphData {
  nodes: Entity[];
  connections: EntityConnection[];
}

// Detailed Graph Types for Lazy Loading
export interface DetailedGraphNode {
  primary_id: string;
  site_id: string;
  component_type: string | null;
  properties: Record<string, unknown> | null;
  aliases: string[] | null;
  configuration: Record<string, unknown> | null;
  pref: Record<string, unknown> | null;
  component_order: number | null;
  tier: number;
  child_count: number;
  total_descendants: number;
  is_expanded: boolean;
}

export interface DetailedGraphEdge {
  id: string;
  source_id: string;
  target_id: string;
  relation: string | null;
  signal: string | null;
  properties: Record<string, unknown> | null;
  configuration: Record<string, unknown> | null;
}

export interface InitialViewDetailedResponse {
  nodes: DetailedGraphNode[];
  edges: DetailedGraphEdge[];
  total_node_count: number;
  root_ids: string[];
}

export interface ExpandDetailedResponse {
  parent_id: string;
  children: DetailedGraphNode[];
  edges: DetailedGraphEdge[];
}

export interface GetInitialViewDetailedParams {
  portfolio_id: string;
  site_id: string;
  max_tier?: number;
  exclude_modules?: boolean;
  include_below_inverter?: boolean;
}

export interface ExpandNodeDetailedParams {
  portfolio_id: string;
  site_id: string;
  node_id: string;
  depth?: number;
  exclude_modules?: boolean;
}

// Lightweight Graph Types (slim payload for fast network visualization)
export interface LightweightGraphNode {
  uuid: string;
  primary_id: string;
  component_type: string | null;
}

export interface LightweightGraphEdge {
  id: string;
  source_id: string;
  target_id: string;
  relation: string | null;
  signal: string | null;
}

export interface LightweightGraphResponse {
  nodes: LightweightGraphNode[];
  edges: LightweightGraphEdge[];
  total_node_count: number;
  root_ids: string[];
}

export interface GetLightweightGraphParams {
  portfolio_id: string;
  site_id: string;
  include_below_inverter?: boolean;
}

export interface GetEntityDetailsParams {
  portfolio_id: string;
  site_id: string;
  entity_id: string;
}

// Component List Types (for component specifications view - single request)
export interface ComponentListItem {
  uuid: string;
  primary_id: string;
  component_type: string | null;
  properties: Record<string, unknown> | null;
}

export interface ComponentListResponse {
  items: ComponentListItem[];
  total_count: number;
}

export interface GetComponentListParams {
  portfolio_id: string;
  site_id: string;
  exclude_modules?: boolean;
}

export const entityApi = apiClient.injectEndpoints({
  endpoints: (builder) => ({
    // List entities for a site (parallel pagination for performance)
    listEntities: builder.query<Entity[], ListEntitiesParams>({
      queryFn: async ({ portfolio_id, site_id, exclude_modules }, _queryApi, _extraOptions, baseQuery) => {
        const pageSize = 1000; // Max allowed by backend

        // Build params object - only include exclude_modules if it's true
        const baseParams: Record<string, any> = { limit: pageSize, offset: 0 };
        if (exclude_modules === true) {
          baseParams.exclude_modules = true;
        }

        // Fetch first page to get total count
        const firstResult = await baseQuery({
          url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/entities`,
          method: 'GET',
          params: baseParams,
        });

        if (firstResult.error) {
          return { error: firstResult.error };
        }

        const firstData = firstResult.data;

        // Handle array response (single page)
        if (Array.isArray(firstData)) {
          return { data: firstData.map(mapEntityResponse) };
        }

        // Handle paginated response
        if (!firstData?.items || !Array.isArray(firstData.items)) {
          return { data: [] };
        }

        const firstResponse = firstData as EntitiesResponse;
        const total = firstResponse.total || firstResponse.items.length;
        const firstPageEntities = firstResponse.items.map(mapEntityResponse);

        // If all data fits in first page, return early
        if (total <= pageSize) {
          return { data: firstPageEntities };
        }

        // Calculate remaining pages and fetch in parallel
        const remainingPages: number[] = [];
        for (let offset = pageSize; offset < total; offset += pageSize) {
          remainingPages.push(offset);
        }

        // Fetch all remaining pages in parallel (max 10 concurrent to avoid overwhelming server)
        const CONCURRENT_LIMIT = 10;
        const allEntities = [...firstPageEntities];

        for (let i = 0; i < remainingPages.length; i += CONCURRENT_LIMIT) {
          const batch = remainingPages.slice(i, i + CONCURRENT_LIMIT);
          const batchResults = await Promise.all(
            batch.map(offset => {
              const pageParams: Record<string, any> = { limit: pageSize, offset };
              if (exclude_modules === true) {
                pageParams.exclude_modules = true;
              }
              return baseQuery({
                url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/entities`,
                method: 'GET',
                params: pageParams,
              });
            })
          );

          for (const pageResult of batchResults) {
            if (pageResult.error) {
              console.warn('Error fetching entity page:', pageResult.error);
              continue;
            }

            const pageData = pageResult.data;
            let pageItems: any[] = [];

            if (Array.isArray(pageData)) {
              pageItems = pageData;
            } else if (pageData?.items && Array.isArray(pageData.items)) {
              pageItems = pageData.items;
            }

            if (pageItems.length > 0) {
              allEntities.push(...pageItems.map(mapEntityResponse));
            }
          }
        }

        return { data: allEntities };
      },
      keepUnusedDataFor: 300, // Cache for 5 minutes (entities rarely change during a session)
      providesTags: (_result, _error, arg) => [
        { type: 'Entity', id: `${arg.portfolio_id}-${arg.site_id}` },
        'Entity',
      ],
    }),

    // List connections for a site (parallel pagination for performance)
    listConnections: builder.query<EntityConnection[], ListConnectionsParams>({
      queryFn: async ({ portfolio_id, site_id }, _queryApi, _extraOptions, baseQuery) => {
        const pageSize = 1000; // Max allowed by backend

        // Fetch first page to get total count
        const firstResult = await baseQuery({
          url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/connections`,
          method: 'GET',
          params: { limit: pageSize, offset: 0, exclude_modules: true },
        });

        if (firstResult.error) {
          return { error: firstResult.error };
        }

        const firstData = firstResult.data;

        // Handle array response (single page)
        if (Array.isArray(firstData)) {
          return { data: firstData.map(mapConnectionResponse) };
        }

        // Handle paginated response
        if (!firstData?.items || !Array.isArray(firstData.items)) {
          return { data: [] };
        }

        const firstResponse = firstData as ConnectionsResponse;
        const total = firstResponse.total || firstResponse.items.length;
        const firstPageConnections = firstResponse.items.map(mapConnectionResponse);

        // If all data fits in first page, return early
        if (total <= pageSize) {
          return { data: firstPageConnections };
        }

        // Calculate remaining pages and fetch in parallel
        const remainingPages: number[] = [];
        for (let offset = pageSize; offset < total; offset += pageSize) {
          remainingPages.push(offset);
        }

        // Fetch all remaining pages in parallel (max 10 concurrent)
        const CONCURRENT_LIMIT = 10;
        const allConnections = [...firstPageConnections];

        for (let i = 0; i < remainingPages.length; i += CONCURRENT_LIMIT) {
          const batch = remainingPages.slice(i, i + CONCURRENT_LIMIT);
          const batchResults = await Promise.all(
            batch.map(offset =>
              baseQuery({
                url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/connections`,
                method: 'GET',
                params: { limit: pageSize, offset, exclude_modules: true },
              })
            )
          );

          for (const pageResult of batchResults) {
            if (pageResult.error) {
              console.warn('Error fetching connections page:', pageResult.error);
              continue;
            }

            const pageData = pageResult.data;
            let pageItems: any[] = [];

            if (Array.isArray(pageData)) {
              pageItems = pageData;
            } else if (pageData?.items && Array.isArray(pageData.items)) {
              pageItems = pageData.items;
            }

            if (pageItems.length > 0) {
              allConnections.push(...pageItems.map(mapConnectionResponse));
            }
          }
        }

        return { data: allConnections };
      },
      keepUnusedDataFor: 300, // Cache for 5 minutes (connections rarely change during a session)
      providesTags: (_result, _error, arg) => [
        { type: 'Connection', id: `${arg.portfolio_id}-${arg.site_id}` },
        'Connection',
      ],
    }),

    // Get a specific entity
    getEntity: builder.query<Entity, { entity_id: string }>({
      query: ({ entity_id }) => ({
        url: `/api/v1/entities/${entity_id}`,
        method: 'GET',
      }),
      transformResponse: (entity: any): Entity => {
        const mapped = mapEntityResponse(entity);
        // Add additional fields that may be needed for single entity queries
        return {
          ...mapped,
          asset_tag: entity.serial_number || mapped.serial_number,
          location: entity.latitude && entity.longitude ? {
            type: 'Point',
            coordinates: [entity.longitude, entity.latitude]
          } : null
        };
      },
    }),

    // Incremental loading: Fetch a subset of entities with pagination
    // Useful for initial load or loading entities in chunks
    listEntitiesIncremental: builder.query<EntitiesResponse, ListEntitiesIncrementalParams>({
      query: ({ portfolio_id, site_id, limit, offset, concept_id }) => ({
        url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/entities`,
        method: 'GET',
        params: {
          limit,
          offset,
          exclude_modules: true,
          ...(concept_id && { concept_id }),
        },
      }),
      transformResponse: (response: any): EntitiesResponse => {
        // Handle response format
        if (Array.isArray(response)) {
          return {
            items: response.map(mapEntityResponse),
            total: response.length,
            limit: response.length,
            offset: 0,
          };
        } else if (response?.items && Array.isArray(response.items)) {
          return {
            items: response.items.map(mapEntityResponse),
            total: response.total || response.items.length,
            limit: response.limit || response.items.length,
            offset: response.offset || 0,
          };
        } else {
          return {
            items: [],
            total: 0,
            limit: 0,
            offset: 0,
          };
        }
      },
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, arg) => [
        { type: 'Entity', id: `${arg.portfolio_id}-${arg.site_id}-incremental-${arg.offset}` },
        'Entity',
      ],
    }),

    // Incremental loading: Fetch connections for specific entity IDs
    // Useful for loading connections on-demand when entities become visible
    listConnectionsForEntities: builder.query<EntityConnection[], ListConnectionsForEntitiesParams>({
      queryFn: async ({ portfolio_id, site_id, entity_ids }, _queryApi, _extraOptions, baseQuery) => {
        // If no entity IDs provided, return empty array
        if (!entity_ids || entity_ids.length === 0) {
          return { data: [] };
        }

        // Fetch connections for each entity ID
        // Note: This assumes the backend supports filtering by entity_id
        // If not, we may need to fetch all and filter client-side
        const allConnections: EntityConnection[] = [];
        const pageSize = 1000;

        for (const entityId of entity_ids) {
          let offset = 0;
          let hasMore = true;

          while (hasMore) {
            const result = await baseQuery({
              url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/connections`,
              method: 'GET',
              params: {
                limit: pageSize,
                offset,
                exclude_modules: true,
                entity_id: entityId,
              },
            });

            if (result.error) {
              // Log error but continue with other entities
              console.warn(`Error fetching connections for entity ${entityId}:`, result.error);
              break;
            }

            const data = result.data;
            let items: any[] = [];

            if (Array.isArray(data)) {
              items = data;
            } else if (data?.items && Array.isArray(data.items)) {
              items = data.items;
            }

            if (items.length > 0) {
              const connections = items
                .map(mapConnectionResponse)
                .filter(
                  conn =>
                    conn.source_entity_id === entityId ||
                    conn.target_entity_id === entityId
                );
              allConnections.push(...connections);
            }

            hasMore = items.length === pageSize;
            offset += pageSize;
          }
        }

        // Remove duplicates (connections might appear for both source and target)
        const uniqueConnections = Array.from(
          new Map(allConnections.map(conn => [conn.id, conn])).values()
        );

        return { data: uniqueConnections };
      },
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, arg) => [
        { type: 'Connection', id: `${arg.portfolio_id}-${arg.site_id}-for-entities` },
        'Connection',
      ],
    }),

    // Get site graph (single request - optimal for network visualization)
    // This endpoint returns both nodes and edges in one request, avoiding pagination overhead
    getSiteGraph: builder.query<SiteGraphData, GetSiteGraphParams>({
      query: ({ portfolio_id, site_id, exclude_modules = true }) => ({
        url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/graph`,
        method: 'GET',
        params: { exclude_modules },
      }),
      transformResponse: (response: any): SiteGraphData => {
        // Map backend graph response to frontend format
        const nodes: Entity[] = (response.nodes || []).map((node: any) => ({
          id: node.id,
          name: node.label,
          concept_id: node.component_type,
          site_id: '',
          tenant_id: '',
          parent_entity_id: null,
          description: null,
          manufacturer: null,
          model: null,
          serial_number: null,
          installation_date: null,
          warranty_expiry: null,
          status: 'active',
          latitude: null,
          longitude: null,
          altitude: null,
          orientation: null,
          tilt: null,
          properties: node.properties || {},
          version: 1,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          // Legacy field mappings for compatibility
          entity_id: node.id,
          entity_name: node.label,
          entity_type_id: node.component_type,
          entity_type_name: node.component_type,
          concept_name: node.component_type,
          concept_category: node.component_type,
          pvcollada_component_type: node.component_type,
          metadata: node.properties || {},
          entity_metadata: node.properties || {},
          component_order: node.properties?.component_order,
          aliases: [],
          configuration: {},
          pref: node.properties?.pref,
        }));

        const connections: EntityConnection[] = (response.edges || []).map((edge: any) => ({
          id: edge.id,
          tenant_id: '',
          site_id: '',
          source_entity_id: edge.source,
          target_entity_id: edge.target,
          connection_type: edge.relation || 'connects',
          properties: {
            signal: edge.signal,
          },
          configuration: {},
          created_at: new Date().toISOString(),
          source_entity_name: '',
          target_entity_name: '',
        }));

        return { nodes, connections };
      },
      keepUnusedDataFor: 300, // Cache for 5 minutes
      providesTags: (_result, _error, arg) => [
        { type: 'Entity', id: `${arg.portfolio_id}-${arg.site_id}-graph` },
        { type: 'Connection', id: `${arg.portfolio_id}-${arg.site_id}-graph` },
        'Entity',
        'Connection',
      ],
    }),

    // Get graph preview for lazy-loading network visualization
    getInitialViewDetailed: builder.query<InitialViewDetailedResponse, GetInitialViewDetailedParams>({
      query: ({ portfolio_id, site_id, max_tier, exclude_modules = true, include_below_inverter = false }) => ({
        url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/graph/preview`,
        method: 'GET',
        params: { ...(max_tier != null ? { max_tier } : {}), exclude_modules, include_below_inverter },
      }),
      keepUnusedDataFor: 300, // Cache for 5 minutes
      providesTags: (_result, _error, arg) => [
        { type: 'Entity', id: `${arg.portfolio_id}-${arg.site_id}-preview` },
        'Entity',
      ],
    }),

    // Expand a node to get its children with full details
    expandNodeDetailed: builder.query<ExpandDetailedResponse, ExpandNodeDetailedParams>({
      query: ({ portfolio_id, site_id, node_id, depth = 1, exclude_modules = true }) => ({
        url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/graph/nodes/${node_id}/expand-detailed`,
        method: 'GET',
        params: { depth, exclude_modules },
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, arg) => [
        { type: 'Entity', id: `${arg.portfolio_id}-${arg.site_id}-expand-${arg.node_id}` },
        'Entity',
      ],
    }),

    // Get initial view graph for fast network visualization (slim payload)
    // Returns only uuid, primary_id, component_type for nodes and minimal edge data
    getInitialViewGraph: builder.query<LightweightGraphResponse, GetLightweightGraphParams>({
      query: ({ portfolio_id, site_id, include_below_inverter = false }) => ({
        url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/graph/initial-view`,
        method: 'GET',
        params: { include_below_inverter },
      }),
      keepUnusedDataFor: 300, // Cache for 5 minutes
      providesTags: (_result, _error, arg) => [
        { type: 'Entity', id: `${arg.portfolio_id}-${arg.site_id}-initial-view` },
        'Entity',
      ],
    }),

    // Get entity details on-demand (for when a node is selected)
    getEntityDetails: builder.query<Entity, GetEntityDetailsParams>({
      query: ({ portfolio_id, site_id, entity_id }) => ({
        url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/entities/${entity_id}`,
        method: 'GET',
      }),
      transformResponse: mapEntityResponse,
      keepUnusedDataFor: 300, // Cache for 5 minutes
      providesTags: (_result, _error, arg) => [
        { type: 'Entity', id: arg.entity_id },
        'Entity',
      ],
    }),

    // Get component list for component specifications view (single request, no pagination)
    getComponentList: builder.query<ComponentListResponse, GetComponentListParams>({
      query: ({ portfolio_id, site_id, exclude_modules = true }) => ({
        url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/components`,
        method: 'GET',
        params: { exclude_modules },
      }),
      keepUnusedDataFor: 300, // Cache for 5 minutes
      providesTags: (_result, _error, arg) => [
        { type: 'Entity', id: `${arg.portfolio_id}-${arg.site_id}-components` },
        'Entity',
      ],
    }),

    // Single-page entity fetch (no auto-pagination). Use for dashboard summaries
    // where you need a bounded number of entities including modules.
    listEntitiesPage: builder.query<EntitiesResponse, ListEntitiesPageParams>({
      query: ({ portfolio_id, site_id, limit, offset = 0, exclude_modules = false }) => ({
        url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/entities`,
        method: 'GET',
        params: {
          limit,
          offset,
          ...(exclude_modules && { exclude_modules: true }),
        },
      }),
      transformResponse: (response: any): EntitiesResponse => {
        if (Array.isArray(response)) {
          return {
            items: response.map(mapEntityResponse),
            total: response.length,
            limit: response.length,
            offset: 0,
          };
        } else if (response?.items && Array.isArray(response.items)) {
          return {
            items: response.items.map(mapEntityResponse),
            total: response.total || response.items.length,
            limit: response.limit || response.items.length,
            offset: response.offset || 0,
          };
        }
        return { items: [], total: 0, limit: 0, offset: 0 };
      },
      providesTags: (_result, _error, arg) => [
        { type: 'Entity', id: `${arg.portfolio_id}-${arg.site_id}-page` },
      ],
    }),
  }),
  overrideExisting: true,
});

export const {
  useListEntitiesQuery,
  useLazyListEntitiesQuery,
  useListConnectionsQuery,
  useLazyListConnectionsQuery,
  useGetEntityQuery,
  useListEntitiesIncrementalQuery,
  useLazyListEntitiesIncrementalQuery,
  useListConnectionsForEntitiesQuery,
  useLazyListConnectionsForEntitiesQuery,
  useGetSiteGraphQuery,
  useLazyGetSiteGraphQuery,
  useGetInitialViewDetailedQuery,
  useLazyGetInitialViewDetailedQuery,
  useExpandNodeDetailedQuery,
  useLazyExpandNodeDetailedQuery,
  // Initial view graph (slim payload for fast network visualization)
  useGetInitialViewGraphQuery,
  useLazyGetInitialViewGraphQuery,
  // Entity details on-demand
  useGetEntityDetailsQuery,
  useLazyGetEntityDetailsQuery,
  // Component list (single request for component specifications view)
  useGetComponentListQuery,
  useLazyGetComponentListQuery,
  useListEntitiesPageQuery,
  useLazyListEntitiesPageQuery,
} = entityApi;

